/*
 * GC · Gold Coast landing page
 * Draws the tape-measure hero ([data-gc-tape]) and the ruler dividers ([data-gc-rule]).
 * Included by gc-hero and gc-ruler; safe to load more than once.
 * Re-initialises on shopify:section:load and cleans up on shopify:section:unload.
 */
(function () {
  'use strict';
  if (window.GCLanding) return;

  var instances = [];
  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500];

  function reducedMotion() { return !!(motionQuery && motionQuery.matches); }
  function num(value, fallback) { var n = parseFloat(value); return isNaN(n) ? fallback : n; }
  function cssVar(el, name) { return (window.getComputedStyle(el).getPropertyValue(name) || '').trim(); }
  function cssNum(el, name, fallback) { return num(cssVar(el, name), fallback); }
  function ease(t) { return 1 - Math.pow(1 - t, 3); }
  function niceStep(min) {
    for (var i = 0; i < STEPS.length; i++) { if (STEPS[i] >= min) return STEPS[i]; }
    return STEPS[STEPS.length - 1];
  }
  function fontFrom(el, varName, fallbackEl) {
    var f = cssVar(el, varName);
    if (!f || f.indexOf('var(') !== -1) f = window.getComputedStyle(fallbackEl || el).fontFamily;
    return f || 'serif';
  }
  function prep(canvas) {
    var rect = canvas.getBoundingClientRect();
    var w = rect.width, h = rect.height;
    if (!w || !h) return null;
    var dpr = window.devicePixelRatio || 1;
    var pw = Math.round(w * dpr), ph = Math.round(h * dpr);
    if (canvas.width !== pw) canvas.width = pw;
    if (canvas.height !== ph) canvas.height = ph;
    var ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    return { ctx: ctx, w: w, h: h };
  }
  function dot(ctx, x, y, r, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  /* ---------------------------------------------------------------- Tape measure */
  function Tape(el) {
    var d = el.dataset;
    this.el = el;
    this.canvas = el.querySelector('canvas');
    this.readout = el.querySelector('[data-gc-read]');
    this.caption = el.querySelector('[data-gc-caption]');
    this.distance = Math.max(1, num(d.distance, 80));
    this.max = this.distance + Math.max(0, num(d.extra, 0));
    this.every = Math.max(1, num(d.every, 10));
    this.unit = d.unit || '';
    this.duration = Math.max(200, num(d.duration, 1800));
    this.animated = d.animate === 'true' && !reducedMotion();
    this.progress = this.animated ? 0 : 1;
    this.raf = null;
    this.io = null;
    this.draw();
    if (this.animated) this.observe();
  }

  Tape.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this.play(); return; }
    this.io = new IntersectionObserver(function (entries) {
      if (entries[0] && entries[0].isIntersecting) {
        self.io.disconnect();
        self.io = null;
        self.play();
      }
    }, { threshold: 0.4 });
    this.io.observe(this.el);
  };

  Tape.prototype.play = function () {
    var self = this, start = null;
    function frame(time) {
      if (start === null) start = time;
      self.progress = Math.min(1, (time - start) / self.duration);
      self.draw();
      self.raf = self.progress < 1 ? window.requestAnimationFrame(frame) : null;
    }
    this.raf = window.requestAnimationFrame(frame);
  };

  Tape.prototype.inset = function (w) {
    if (this.el.classList.contains('gc-trip--full')) {
      var contentW = cssNum(this.el, '--gc-w', 1180);
      var sidePad = cssNum(this.el, '--gc-px', 20);
      return Math.max(0, (w - contentW - sidePad * 2) / 2) + sidePad;
    }
    return cssNum(this.el, '--gc-tape-inset', 20);
  };

  Tape.prototype.draw = function () {
    if (!this.canvas) return;
    var g = prep(this.canvas);
    if (!g) return;
    var ctx = g.ctx, w = g.w, h = g.h, d = this.el.dataset;
    var inset = this.inset(w);
    var span = Math.max(1, w - inset * 2);
    var max = this.max;
    var x = function (v) { return inset + span * (v / max); };
    var perUnit = span / max;

    var minor = niceStep(5 / perUnit);
    var every = this.every;
    while (every * perUnit < 30) every *= 2;
    var half = every / 2;

    var base = Math.round(h * 0.42) + 0.5;
    var majorLen = Math.min(26, h * 0.2);
    var midLen = majorLen * 0.62;
    var minorLen = majorLen * 0.31;

    var fontH = fontFrom(this.el, '--gc-font-h', this.readout || this.el);
    var fontL = fontFrom(this.el, '--gc-font-l', this.caption || this.el);
    var labelSize = num(d.labelSize, 16);
    var subSize = num(d.subSize, 11);

    // baseline
    ctx.lineWidth = 1;
    ctx.strokeStyle = d.major;
    ctx.beginPath();
    ctx.moveTo(inset, base);
    ctx.lineTo(w - inset, base);
    ctx.stroke();

    // small and middle ticks
    var k, px;
    for (k = 0; k <= max + 1e-6; k += minor) {
      if (k % every === 0) continue;
      var isMid = half >= minor && k % half === 0;
      px = Math.round(x(k)) + 0.5;
      ctx.strokeStyle = d.tick;
      ctx.beginPath();
      ctx.moveTo(px, base);
      ctx.lineTo(px, base - (isMid ? midLen : minorLen));
      ctx.stroke();
    }

    // long ticks with numbers
    ctx.font = subSize + 'px ' + fontL;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    for (k = 0; k <= max + 1e-6; k += every) {
      px = Math.round(x(k)) + 0.5;
      ctx.strokeStyle = d.major;
      ctx.beginPath();
      ctx.moveTo(px, base);
      ctx.lineTo(px, base - majorLen);
      ctx.stroke();
      ctx.fillStyle = d.muted;
      ctx.fillText(String(k), px, base - majorLen - 6);
    }

    // travelled distance
    var current = this.distance * ease(this.progress);
    ctx.strokeStyle = d.progress;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(inset, base);
    ctx.lineTo(x(current), base);
    ctx.stroke();
    ctx.lineWidth = 1;
    dot(ctx, x(0), base, 5, d.text);
    if (this.progress > 0) dot(ctx, x(current), base, 5, d.progress);

    // end labels
    var labelY = base + labelSize + 14;
    var subY = labelY + subSize + 6;
    ctx.textAlign = 'left';
    ctx.font = labelSize + 'px ' + fontH;
    ctx.fillStyle = d.text;
    ctx.fillText(d.start || '', x(0), labelY);
    ctx.font = subSize + 'px ' + fontL;
    ctx.fillStyle = d.muted;
    ctx.fillText(d.startSub || '', x(0), subY);

    var endX = Math.min(x(this.distance) + 6, w - 4);
    ctx.textAlign = 'right';
    ctx.font = labelSize + 'px ' + fontH;
    ctx.fillStyle = this.progress >= 1 ? d.progress : d.muted;
    ctx.fillText(d.end || '', endX, labelY);
    ctx.font = subSize + 'px ' + fontL;
    ctx.fillStyle = d.muted;
    ctx.fillText(d.endSub || '', endX, subY);

    if (this.readout) {
      this.readout.textContent = Math.round(current) + (this.unit ? ' ' + this.unit : '');
    }
  };

  Tape.prototype.destroy = function () {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    this.raf = null;
    this.io = null;
  };

  /* ---------------------------------------------------------------- Ruler divider */
  function Rule(el) {
    this.el = el;
    this.canvas = el;
    this.draw();
  }

  Rule.prototype.draw = function () {
    var g = prep(this.canvas);
    if (!g) return;
    var ctx = g.ctx, w = g.w, h = g.h, d = this.el.dataset;
    var inset = cssNum(this.el, '--gc-rule-inset', 20);
    var gap = Math.max(2, cssNum(this.el, '--gc-rule-gap', 8));
    var every = Math.max(2, Math.round(num(d.every, 10)));
    var half = every / 2;
    var lenMinor = num(d.minor, 5), lenMid = num(d.mid, 9), lenMajor = num(d.major, 14);
    var lineW = Math.max(1, num(d.lineWidth, 1));
    var dir = d.direction || 'up';
    var offset = lineW % 2 ? 0.5 : 0;
    var y;
    if (dir === 'down') y = (h - lenMajor) / 2;
    else if (dir === 'both') y = h / 2;
    else y = (h + lenMajor) / 2;
    y = Math.round(y) + offset;

    ctx.lineWidth = lineW;
    if (d.baseline !== 'false') {
      ctx.strokeStyle = d.color;
      ctx.beginPath();
      ctx.moveTo(inset, y);
      ctx.lineTo(w - inset, y);
      ctx.stroke();
    }
    for (var i = 0, xx = inset; xx <= w - inset + 0.01; i++, xx += gap) {
      var isMajor = i % every === 0;
      var isMid = !isMajor && i % half === 0;
      var len = isMajor ? lenMajor : isMid ? lenMid : lenMinor;
      var px = Math.round(xx) + offset;
      ctx.strokeStyle = isMajor ? d.majorColor : d.color;
      ctx.beginPath();
      if (dir === 'down') { ctx.moveTo(px, y); ctx.lineTo(px, y + len); }
      else if (dir === 'both') { ctx.moveTo(px, y - len / 2); ctx.lineTo(px, y + len / 2); }
      else { ctx.moveTo(px, y); ctx.lineTo(px, y - len); }
      ctx.stroke();
    }
  };

  Rule.prototype.destroy = function () {};

  /* ---------------------------------------------------------------- Lifecycle */
  function init(root) {
    var scope = root || document;
    var tapes = scope.querySelectorAll('[data-gc-tape]');
    var rules = scope.querySelectorAll('[data-gc-rule]');
    var i;
    for (i = 0; i < tapes.length; i++) {
      if (!tapes[i].__gcInstance) { tapes[i].__gcInstance = new Tape(tapes[i]); instances.push(tapes[i].__gcInstance); }
    }
    for (i = 0; i < rules.length; i++) {
      if (!rules[i].__gcInstance) { rules[i].__gcInstance = new Rule(rules[i]); instances.push(rules[i].__gcInstance); }
    }
  }

  function destroy(root) {
    instances = instances.filter(function (inst) {
      if (root && root.contains(inst.el)) {
        inst.destroy();
        inst.el.__gcInstance = null;
        return false;
      }
      return true;
    });
  }

  function redraw() {
    instances = instances.filter(function (inst) { return document.documentElement.contains(inst.el); });
    instances.forEach(function (inst) { inst.draw(); });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(redraw, 120);
  });
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });

  window.GCLanding = { init: init, redraw: redraw };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(redraw);
})();
